import { useQueries } from '@tanstack/react-query';
import { ArrowDownUp, Play } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { AccountAnnualReturns } from '../components/domain/AccountAnnualReturns';
import { CalendarHeatmap } from '../components/domain/CalendarHeatmap';
import { ClosedLotsDrilldown } from '../components/domain/ClosedLotsDrilldown';
import { FlagImpactChart } from '../components/domain/FlagImpactChart';
import { InvestmentPortfolioDashboard } from '../components/domain/InvestmentPortfolioDashboard';
import { LongTermAccountsComparison } from '../components/domain/LongTermAccountsComparison';
import { MonthlyPnlGrid } from '../components/domain/MonthlyPnlGrid';
import { StatCard } from '../components/domain/StatCard';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { TableCell } from '../components/ui/TableCell';
import { TableHeaderCell } from '../components/ui/TableHeaderCell';
import { accountKeys, useAccounts } from '../features/accounts/queries';
import {
  useAnalyticsBySymbol,
  useAnalyticsCalendar,
  useAnalyticsFlagImpact,
  useAnalyticsOverview,
  usePnlHistory,
} from '../features/analytics/queries';
import { useInvestmentPortfolio } from '../features/investmentTheses/portfolioQueries';
import { mandateLabel } from '../lib/accountMandate';
import {
  portfolioSummary,
  type AnalyticsPeriod,
  type AnalyticsSymbolDto,
} from '../lib/api/endpoints';
import { formatCurrency } from '../lib/format/currency';
import { positionReplayHref } from '../lib/navigation/positionReplay';

const periods: Array<{ value: AnalyticsPeriod; label: string }> = [
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: 'ytd', label: 'YTD' },
  { value: 'all', label: 'ALL' },
];

type SymbolSort = 'totalPnl' | 'tradeCount' | 'winRate' | 'avgHoldDays';
type AnalyticsView = 'trading' | 'investing' | 'all-wealth';

const dashboardViews: Array<{ value: AnalyticsView; label: string; helper: string }> = [
  { value: 'trading', label: 'Trading', helper: 'Process and realised performance' },
  { value: 'investing', label: 'Investing', helper: 'Portfolio growth and thesis fit' },
  { value: 'all-wealth', label: 'All Wealth', helper: 'Cross-account concentration and exposure' },
];

interface Drilldown {
  from: string;
  to: string;
  label: string;
  year: number;
  month: number | null;
}

const monthFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

function currentYear(): number {
  return new Date().getUTCFullYear();
}

function isoDay(year: number, monthIndex: number, day: number): string {
  return new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10);
}

function monthDrilldown(year: number, monthIndex: number): Drilldown {
  return {
    from: isoDay(year, monthIndex, 1),
    to: isoDay(year, monthIndex + 1, 0),
    label: monthFormatter.format(new Date(Date.UTC(year, monthIndex, 1))),
    year,
    month: monthIndex,
  };
}

function dayDrilldown(date: string): Drilldown {
  return { from: date, to: date, label: date, year: Number(date.slice(0, 4)), month: null };
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function pnlTone(value: number): 'danger' | 'neutral' | 'success' {
  if (value === 0) return 'neutral';
  return value > 0 ? 'success' : 'danger';
}

function sortSymbols(
  symbols: AnalyticsSymbolDto[] | undefined,
  sortKey: SymbolSort,
  descending: boolean,
): AnalyticsSymbolDto[] {
  const direction = descending ? -1 : 1;
  return [...(symbols ?? [])].sort((left, right) => {
    const delta = left[sortKey] - right[sortKey];
    return delta === 0 ? left.symbol.localeCompare(right.symbol) : delta * direction;
  });
}

function averageHoldDays(symbols: AnalyticsSymbolDto[]): number {
  if (symbols.length === 0) return 0;
  const total = symbols.reduce((sum, symbol) => sum + symbol.avgHoldDays, 0);
  return total / symbols.length;
}

function segmentSignal(symbol: AnalyticsSymbolDto): string {
  if (symbol.tradeCount === 0) return 'No signal';
  if (symbol.totalPnl > 0 && symbol.winRate >= 60) return 'Best edge';
  if (symbol.winRate >= 50) return 'Consistent';
  if (symbol.avgHoldDays <= 7) return 'Fast swing';
  if (symbol.avgHoldDays > 21) return 'Trend hold';
  return 'Mixed';
}

export function Analytics() {
  const accounts = useAccounts();
  const accountSummaries = useQueries({
    queries: (accounts.data ?? []).map((account) => ({
      queryKey: accountKeys.portfolioSummary(account.id),
      queryFn: () => portfolioSummary(account.id),
      enabled: Boolean(account.id),
    })),
  });
  const investmentPortfolio = useInvestmentPortfolio();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPeriod = searchParams.get('period') as AnalyticsPeriod | null;
  const requestedYear = searchParams.get('year');
  const [period, setPeriodState] = useState<AnalyticsPeriod>(requestedPeriod ?? '30d');
  const [sortKey, setSortKey] = useState<SymbolSort>('totalPnl');
  const [descending, setDescending] = useState(true);
  const [calendarYear, setCalendarYearState] = useState<number>(
    requestedYear ? Number(requestedYear) : currentYear(),
  );

  useEffect(() => {
    const nextPeriod = (searchParams.get('period') as AnalyticsPeriod | null) ?? '30d';
    const nextYear = Number(searchParams.get('year') ?? String(currentYear()));
    setPeriodState(nextPeriod);
    setCalendarYearState(Number.isFinite(nextYear) ? nextYear : currentYear());
  }, [searchParams]);
  const [drilldown, setDrilldown] = useState<Drilldown | null>(null);
  const requestedView = dashboardViews.some((view) => view.value === searchParams.get('view'))
    ? (searchParams.get('view') as AnalyticsView)
    : 'trading';
  const view = requestedView;
  const filteredAccounts = (accounts.data ?? []).filter((account) => {
    if (view === 'investing') {
      return account.mandate === 'LONG_TERM_INVESTING' || !account.mandate;
    }
    if (view === 'all-wealth') return true;
    return account.mandate === 'SWING_TRADING' || !account.mandate;
  });
  const selectedAccountId = filteredAccounts.some(
    (account) => account.id === searchParams.get('accountId'),
  )
    ? searchParams.get('accountId')
    : (filteredAccounts[0]?.id ?? null);
  const longTermAccounts = (accounts.data ?? []).filter(
    (item) => item.mandate === 'LONG_TERM_INVESTING',
  );
  const longTermState: 'ready' | 'loading' | 'error' = investmentPortfolio.isError
    ? 'error'
    : investmentPortfolio.data
      ? 'ready'
      : 'loading';

  function openInvestingAccount(accountId: string): void {
    navigate(`/investing?accountId=${encodeURIComponent(accountId)}`);
  }
  const account =
    accounts.data?.find((item) => item.id === selectedAccountId) ?? filteredAccounts[0] ?? null;
  const activeRangeLabel = periods.find((item) => item.value === period)?.label ?? 'ALL';
  const activeMandateLabel =
    view === 'trading'
      ? 'Swing Trading'
      : view === 'investing'
        ? 'Long-Term Investing'
        : 'All Wealth';
  const currency = account?.currency ?? 'USD';
  const overview = useAnalyticsOverview(selectedAccountId, period);
  const calendar = useAnalyticsCalendar(selectedAccountId, calendarYear);
  const monthly = usePnlHistory(
    selectedAccountId,
    `${calendarYear}-01-01`,
    `${calendarYear}-12-31`,
    'month',
  );
  const lastSelectableMonth = calendarYear < currentYear() ? 11 : new Date().getUTCMonth();
  const symbols = useAnalyticsBySymbol(selectedAccountId, period);
  const flagImpact = useAnalyticsFlagImpact(selectedAccountId);
  const sortedSymbols = useMemo(
    () => sortSymbols(symbols.data, sortKey, descending),
    [descending, sortKey, symbols.data],
  );
  const curveData = overview.data?.cumulativePnl ?? [];

  function updateSearchParams(nextView: AnalyticsView, nextAccountId: string | null): void {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set('view', nextView);
      if (nextAccountId) {
        params.set('accountId', nextAccountId);
      } else {
        params.delete('accountId');
      }
      return params;
    });
  }

  function updatePeriod(nextPeriod: AnalyticsPeriod): void {
    setPeriodState(nextPeriod);
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set('period', nextPeriod);
      return params;
    });
  }

  function updateCalendarYear(nextYear: number): void {
    setCalendarYearState(nextYear);
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set('year', String(nextYear));
      return params;
    });
  }

  if (accounts.isLoading) {
    return <main className="p-6 text-text-muted">Loading analytics...</main>;
  }

  if (accounts.isError) {
    return <main className="p-6 text-danger">Could not load analytics.</main>;
  }

  return (
    <main className="min-h-screen bg-bg text-text">
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Analytics cockpit</h1>
            <p className="mt-1 text-sm text-text-muted">
              A discipline-first view of trading process, investing progress and all-wealth
              exposure.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div
              className="inline-flex rounded border border-border bg-surface p-1"
              aria-label="Analytics views"
              role="tablist"
            >
              {dashboardViews.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  role="tab"
                  aria-selected={view === item.value}
                  onClick={() => updateSearchParams(item.value, selectedAccountId)}
                  className="rounded px-3 py-2 text-sm text-text-muted transition-colors aria-selected:bg-surface-raised aria-selected:text-text"
                >
                  {item.label}
                </button>
              ))}
            </div>
            {view === 'trading' && filteredAccounts.length > 1 ? (
              <label className="flex items-center gap-2 text-sm text-text-muted">
                <span>Account</span>
                <select
                  value={selectedAccountId ?? ''}
                  onChange={(event) => updateSearchParams(view, event.target.value || null)}
                  className="rounded border border-border bg-surface px-3 py-2 text-text"
                  aria-label="Select analytics account"
                >
                  {filteredAccounts.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        </header>

        <section className="rounded border border-border bg-surface p-3">
          <div className="flex flex-wrap items-center gap-2 text-sm text-text-muted">
            <span className="rounded border border-border bg-surface-raised px-2 py-1 font-medium text-text">
              {activeMandateLabel}
            </span>
            <span className="rounded border border-border bg-surface-raised px-2 py-1">
              {view === 'investing'
                ? `All long-term accounts (${longTermAccounts.length})`
                : view === 'all-wealth'
                  ? `All accounts (${accounts.data?.length ?? 0})`
                  : account
                    ? `Account: ${account.name}`
                    : 'No account selected'}
            </span>
            {view === 'trading' ? (
              <span className="rounded border border-border bg-surface-raised px-2 py-1">
                Range: {activeRangeLabel}
              </span>
            ) : null}
            {view === 'trading' && filteredAccounts.length > 1 ? (
              <span className="rounded border border-border bg-surface-raised px-2 py-1">
                {filteredAccounts.length} accounts in scope
              </span>
            ) : null}
          </div>
        </section>

        {view === 'trading' ? (
          <>
            {filteredAccounts.length === 0 ? (
              <section className="rounded border border-border bg-surface p-4 text-sm text-text-muted">
                No swing-trading accounts match this analytics view yet.
              </section>
            ) : null}
            {!selectedAccountId ? (
              <section className="rounded border border-border bg-surface p-4 text-sm text-text-muted">
                Create an account and import closed trades to see analytics.
              </section>
            ) : (
              <>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div
                    className="inline-flex rounded border border-border"
                    aria-label="Analytics period"
                  >
                    {periods.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        aria-pressed={period === item.value}
                        onClick={() => updatePeriod(item.value)}
                        className="px-3 py-2 text-sm text-text-muted aria-pressed:bg-surface-raised aria-pressed:text-text"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                  <span className="text-sm text-text-muted">
                    {account
                      ? `${account.name} • ${account.mandate ? mandateLabel(account.mandate) : 'Unassigned'}`
                      : ''}
                  </span>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <StatCard
                    label="Total P&L"
                    value={formatCurrency(overview.data?.totalPnl, currency)}
                    tone={(overview.data?.totalPnl ?? 0) >= 0 ? 'good' : 'bad'}
                  />
                  <StatCard label="Win Rate" value={formatPercent(overview.data?.winRate ?? 0)} />
                  <StatCard
                    label="Profit Factor"
                    value={(overview.data?.profitFactor ?? 0).toFixed(2)}
                  />
                  <StatCard label="Avg R" value={(overview.data?.avgRMultiple ?? 0).toFixed(2)} />
                  <StatCard
                    label="Best Day"
                    value={formatCurrency(overview.data?.bestDay.pnl, currency)}
                    delta={overview.data?.bestDay.date || undefined}
                    tone="good"
                  />
                  <StatCard
                    label="Worst Day"
                    value={formatCurrency(overview.data?.worstDay.pnl, currency)}
                    delta={overview.data?.worstDay.date || undefined}
                    tone="bad"
                  />
                </div>

                <section className="rounded border border-border bg-surface p-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-lg font-semibold">Cumulative P&L</h2>
                    <span className="text-sm text-text-muted">
                      {overview.data?.tradeCount ?? 0} closed lots
                    </span>
                  </div>
                  {curveData.length === 0 ? (
                    <p className="mt-4 text-sm text-text-muted">No realised P&L in this period.</p>
                  ) : (
                    <div
                      className="mt-4 h-80"
                      role="img"
                      aria-label="Cumulative realised P&L line chart."
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={curveData} margin={{ left: 0, right: 16, top: 8 }}>
                          <CartesianGrid stroke="#2A3142" vertical={false} />
                          <XAxis dataKey="date" stroke="#A1A8B8" tick={{ fontSize: 12 }} />
                          <YAxis
                            stroke="#A1A8B8"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => formatCurrency(Number(value), currency)}
                            width={88}
                          />
                          <Tooltip
                            contentStyle={{
                              background: '#222837',
                              border: '1px solid #2A3142',
                              color: '#E6E9EF',
                            }}
                            formatter={(value) => formatCurrency(Number(value), currency)}
                          />
                          <Line
                            type="monotone"
                            dataKey="pnl"
                            name="Cumulative P&L"
                            stroke="#60A5FA"
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </section>

                <section className="rounded border border-border bg-surface p-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-lg font-semibold">Process health and risk posture</h2>
                    <span className="text-sm text-text-muted">
                      Execution quality from the recorded trade sample
                    </span>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded border border-border bg-surface-raised p-3">
                      <div className="text-sm text-text-muted">Average trade</div>
                      <div className="mt-2 text-lg font-semibold tabular-nums">
                        {overview.data && overview.data.tradeCount > 0
                          ? formatCurrency(
                              (overview.data.totalPnl ?? 0) / overview.data.tradeCount,
                              currency,
                            )
                          : '—'}
                      </div>
                      <div className="mt-1 text-sm text-text-muted">
                        {overview.data && overview.data.tradeCount > 0
                          ? `${overview.data.tradeCount} completed trades`
                          : 'No closed trade sample'}
                      </div>
                    </div>

                    <div className="rounded border border-border bg-surface-raised p-3">
                      <div className="text-sm text-text-muted">Average holding period</div>
                      <div className="mt-2 text-lg font-semibold tabular-nums">
                        {overview.data && overview.data.avgHoldingDays !== undefined
                          ? `${overview.data.avgHoldingDays.toFixed(1)}d`
                          : '—'}
                      </div>
                      <div className="mt-1 text-sm text-text-muted">
                        Based on completed trade duration in this range
                      </div>
                    </div>

                    <div className="rounded border border-border bg-surface-raised p-3">
                      <div className="text-sm text-text-muted">Execution signal</div>
                      <div className="mt-2 text-lg font-semibold">
                        {flagImpact.data
                          ? flagImpact.data.withFlags.avgPnl >= flagImpact.data.withoutFlags.avgPnl
                            ? 'Flagged trades stronger'
                            : 'Flagged trades weaker'
                          : 'Monitoring'}
                      </div>
                      <div className="mt-1 text-sm text-text-muted">
                        {flagImpact.data
                          ? `${formatCurrency(flagImpact.data.withFlags.avgPnl, currency)} avg flagged P&L`
                          : 'Behavioural flag coverage is not yet available'}
                      </div>
                    </div>

                    <div className="rounded border border-border bg-surface-raised p-3">
                      <div className="text-sm text-text-muted">Risk posture</div>
                      <div className="mt-2 text-lg font-semibold">
                        {overview.data
                          ? overview.data.profitFactor >= 1.5 && overview.data.winRate >= 50
                            ? 'Healthy'
                            : overview.data.profitFactor >= 1 && overview.data.winRate >= 45
                              ? 'Watch'
                              : 'Fragile'
                          : 'No data'}
                      </div>
                      <div className="mt-1 text-sm tabular-nums text-text-muted">
                        {overview.data
                          ? `${overview.data.winRate.toFixed(1)}% win rate · ${overview.data.profitFactor.toFixed(2)} PF`
                          : 'Awaiting trade sample'}
                      </div>
                    </div>
                  </div>
                </section>

                <CalendarHeatmap
                  currency={currency}
                  days={calendar.data}
                  year={calendarYear}
                  maxYear={currentYear()}
                  onYearChange={(year) => {
                    updateCalendarYear(year);
                    setDrilldown(null);
                  }}
                  onDaySelect={(date) => setDrilldown(dayDrilldown(date))}
                />

                <MonthlyPnlGrid
                  currency={currency}
                  points={monthly.data}
                  year={calendarYear}
                  lastSelectableMonth={lastSelectableMonth}
                  selectedMonth={drilldown?.year === calendarYear ? drilldown.month : null}
                  onSelect={(monthIndex) =>
                    setDrilldown((current) =>
                      current?.year === calendarYear && current.month === monthIndex
                        ? null
                        : monthDrilldown(calendarYear, monthIndex),
                    )
                  }
                />

                {drilldown ? (
                  <ClosedLotsDrilldown
                    key={`${selectedAccountId}-${drilldown.from}-${drilldown.to}`}
                    accountId={selectedAccountId}
                    currency={currency}
                    from={drilldown.from}
                    to={drilldown.to}
                    label={drilldown.label}
                    onClose={() => setDrilldown(null)}
                  />
                ) : null}

                <section className="rounded border border-border bg-surface p-4">
                  <h2 className="text-lg font-semibold">Symbol Performance</h2>
                  <div className="mt-4 overflow-x-auto">
                    <Table className="min-w-full">
                      <thead className="text-text-muted">
                        <tr>
                          <TableHeaderCell>Symbol</TableHeaderCell>
                          <SortableHeader
                            active={sortKey === 'totalPnl'}
                            label="Total P&L"
                            onClick={() => toggleSort('totalPnl')}
                          />
                          <SortableHeader
                            active={sortKey === 'tradeCount'}
                            label="Trades"
                            onClick={() => toggleSort('tradeCount')}
                          />
                          <SortableHeader
                            active={sortKey === 'winRate'}
                            label="Win Rate"
                            onClick={() => toggleSort('winRate')}
                          />
                          <SortableHeader
                            active={sortKey === 'avgHoldDays'}
                            label="Avg Hold"
                            onClick={() => toggleSort('avgHoldDays')}
                          />
                          <TableHeaderCell>
                            <span className="sr-only">Actions</span>
                          </TableHeaderCell>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSymbols.map((symbol) => (
                          <tr
                            key={symbol.symbol}
                            className="border-t border-border hover:bg-surface-raised"
                          >
                            <TableCell className="py-3 font-semibold">
                              <Link
                                className="text-primary hover:text-primary-hover"
                                to={`/analytics/symbols/${symbol.symbol}?accountId=${selectedAccountId ?? ''}`}
                              >
                                {symbol.symbol}
                              </Link>
                            </TableCell>
                            <TableCell className="py-3" numeric>
                              <Badge tone={pnlTone(symbol.totalPnl)}>
                                {formatCurrency(symbol.totalPnl, currency)}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-3" numeric>
                              {symbol.tradeCount}
                            </TableCell>
                            <TableCell className="py-3" numeric>
                              {formatPercent(symbol.winRate)}
                            </TableCell>
                            <TableCell className="py-3" numeric>
                              {symbol.avgHoldDays.toFixed(1)}d
                            </TableCell>
                            <TableCell className="py-3" numeric>
                              <Button asChild className="h-8 w-8 px-0" size="sm" variant="ghost">
                                <Link
                                  aria-label={`Replay trades for ${symbol.symbol}`}
                                  to={positionReplayHref(symbol.symbol, selectedAccountId ?? '')}
                                >
                                  <Play className="h-4 w-4" aria-hidden="true" />
                                </Link>
                              </Button>
                            </TableCell>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {sortedSymbols.length === 0 ? (
                      <p className="py-4 text-sm text-text-muted">
                        No closed trades in this period.
                      </p>
                    ) : null}
                  </div>
                </section>

                <section className="rounded border border-border bg-surface p-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-lg font-semibold">Trading edge analysis</h2>
                    <span className="text-sm text-text-muted">
                      Supported by recorded execution and symbol data
                    </span>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded border border-border bg-surface-raised p-3">
                      <div className="text-sm text-text-muted">Top symbol</div>
                      <div className="mt-2 text-lg font-semibold">
                        {sortedSymbols[0] ? sortedSymbols[0].symbol : 'No symbols'}
                      </div>
                      <div className="mt-1 text-sm tabular-nums text-text-muted">
                        {sortedSymbols[0]
                          ? `${formatCurrency(sortedSymbols[0].totalPnl, currency)} · ${sortedSymbols[0].avgHoldDays.toFixed(1)}d`
                          : 'No recorded edge yet'}
                      </div>
                    </div>

                    <div className="rounded border border-border bg-surface-raised p-3">
                      <div className="text-sm text-text-muted">Holding period mix</div>
                      <div className="mt-2 text-lg font-semibold">
                        {sortedSymbols.length > 0
                          ? averageHoldDays(sortedSymbols) <= 7
                            ? 'Short-term edge'
                            : averageHoldDays(sortedSymbols) <= 21
                              ? 'Balanced edge'
                              : 'Long-hold edge'
                          : 'No data'}
                      </div>
                      <div className="mt-1 text-sm tabular-nums text-text-muted">
                        {sortedSymbols.length > 0
                          ? `${averageHoldDays(sortedSymbols).toFixed(1)}d average hold`
                          : 'Awaiting trade history'}
                      </div>
                    </div>

                    <div className="rounded border border-border bg-surface-raised p-3">
                      <div className="text-sm text-text-muted">Execution quality</div>
                      <div className="mt-2 text-lg font-semibold">
                        {flagImpact.data
                          ? flagImpact.data.withFlags.avgPnl >= flagImpact.data.withoutFlags.avgPnl
                            ? 'Mixed by flag'
                            : 'Flagged trades weaker'
                          : 'No flag data'}
                      </div>
                      <div className="mt-1 text-sm tabular-nums text-text-muted">
                        {flagImpact.data
                          ? `${formatCurrency(flagImpact.data.withoutFlags.avgPnl, currency)} without flags`
                          : 'Behavioural flags not yet recorded'}
                      </div>
                    </div>

                    <div className="rounded border border-border bg-surface-raised p-3">
                      <div className="text-sm text-text-muted">Supported dimensions</div>
                      <div className="mt-2 text-lg font-semibold">Ticker</div>
                      <div className="mt-1 text-sm text-text-muted">
                        Symbol, flag impact, holding-period mix and execution quality are currently
                        supported from recorded trade data.
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 rounded border border-border bg-surface p-3">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-text-muted">
                      Edge signals in scope
                    </h3>
                    <ul className="mt-2 space-y-2 text-sm text-text-muted">
                      <li>• Ticker-level edge and realised P&amp;L are directly supported.</li>
                      <li>• Execution quality is shown when behavioural flags are present.</li>
                      <li>• Holding-period profile is derived from recorded average hold days.</li>
                      <li>
                        • Strategy, market and planned-versus-unplanned segments remain gated to
                        reliable inputs rather than inferred labels.
                      </li>
                    </ul>
                  </div>
                </section>

                <section className="rounded border border-border bg-surface p-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-lg font-semibold">Segmented edge breakdown</h2>
                    <span className="text-sm text-text-muted">
                      Ranked by recorded ticker performance and hold profile
                    </span>
                  </div>

                  <div className="mt-4 overflow-x-auto">
                    <Table className="min-w-full">
                      <thead className="text-text-muted">
                        <tr>
                          <TableHeaderCell>Symbol</TableHeaderCell>
                          <TableHeaderCell numeric>Net P&amp;L</TableHeaderCell>
                          <TableHeaderCell numeric>Trades</TableHeaderCell>
                          <TableHeaderCell numeric>Win rate</TableHeaderCell>
                          <TableHeaderCell numeric>Avg hold</TableHeaderCell>
                          <TableHeaderCell numeric>Signal</TableHeaderCell>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSymbols.length === 0 ? (
                          <tr>
                            <TableCell colSpan={6} className="py-4 text-sm text-text-muted">
                              No recorded trade data in this range.
                            </TableCell>
                          </tr>
                        ) : (
                          sortedSymbols.map((symbol) => (
                            <tr
                              key={`${symbol.symbol}-signal`}
                              className="border-t border-border hover:bg-surface-raised"
                            >
                              <TableCell className="py-3 font-semibold">{symbol.symbol}</TableCell>
                              <TableCell numeric className="py-3">
                                <Badge tone={pnlTone(symbol.totalPnl)}>
                                  {formatCurrency(symbol.totalPnl, currency)}
                                </Badge>
                              </TableCell>
                              <TableCell numeric className="py-3">
                                {symbol.tradeCount}
                              </TableCell>
                              <TableCell numeric className="py-3">
                                {formatPercent(symbol.winRate)}
                              </TableCell>
                              <TableCell numeric className="py-3">
                                {symbol.avgHoldDays.toFixed(1)}d
                              </TableCell>
                              <TableCell numeric className="py-3">
                                {segmentSignal(symbol)}
                              </TableCell>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </section>

                <FlagImpactChart currency={currency} impact={flagImpact.data} />
              </>
            )}
          </>
        ) : null}

        {view === 'investing'
          ? (() => {
              const currencyRows = investmentPortfolio.data?.currencies ?? [];
              const portfolioValueText =
                currencyRows.length === 0
                  ? '—'
                  : currencyRows
                      .map((row) => formatCurrency(row.accountValue, row.currency))
                      .join(' · ');
              const strongestTheme =
                currencyRows
                  .flatMap((row) =>
                    row.themes.map((theme) => ({
                      ...theme,
                      currency: row.currency,
                    })),
                  )
                  .filter((theme) => theme.theme)
                  .sort((left, right) => Number(right.value ?? 0) - Number(left.value ?? 0))[0] ??
                null;
              const singleCurrency = currencyRows.length === 1 ? currencyRows[0] : undefined;
              const cashWeightText =
                singleCurrency && singleCurrency.cashWeightPct !== null
                  ? `${Number(singleCurrency.cashWeightPct).toFixed(1)}%`
                  : currencyRows.length > 1
                    ? 'Per currency'
                    : '—';

              return (
                <div className="space-y-5">
                  <section className="rounded border border-border bg-surface p-5">
                    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                      <div>
                        <h2 className="text-xl font-semibold">Long-term investing analytics</h2>
                        <p className="mt-1 text-sm text-text-muted">
                          Thesis coverage, allocation, returns and aggregate holdings for the
                          long-term portfolio.
                        </p>
                      </div>
                      <Link
                        to="/investing"
                        className="text-sm font-medium text-primary hover:text-primary-hover"
                      >
                        Open investing workspace →
                      </Link>
                    </div>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <StatCard label="Accounts" value={String(longTermAccounts.length)} />
                      <StatCard
                        label="Valued accounts"
                        value={String(
                          investmentPortfolio.data?.accounts.filter(
                            (row) => row.valuation.available,
                          ).length ?? 0,
                        )}
                      />
                      <StatCard label="Portfolio value" value={portfolioValueText} tone="good" />
                      <StatCard label="Cash weight" value={cashWeightText} />
                    </div>

                    {strongestTheme ? (
                      <div className="mt-4 rounded border border-border bg-surface-raised p-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <h3 className="text-base font-semibold">Largest theme</h3>
                          <span className="text-sm text-text-muted">
                            Current classified exposure
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
                          <div>
                            <div className="text-xl font-semibold">
                              {strongestTheme.theme ?? 'Unclassified'}
                            </div>
                            <div className="text-sm tabular-nums text-text-muted">
                              {formatCurrency(
                                String(strongestTheme.value ?? '0'),
                                strongestTheme.currency ?? 'USD',
                              )}
                            </div>
                          </div>
                          <div className="text-right text-sm tabular-nums text-text-muted">
                            <div>{Number(strongestTheme.weightPct ?? 0).toFixed(1)}% weight</div>
                            <div>
                              {Number(strongestTheme.unrealizedPnlPct ?? 0).toFixed(1)}% unrealised
                              P&amp;L
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </section>

                  <section className="rounded border border-border bg-surface p-5">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <h2 className="text-lg font-semibold">Thesis and allocation health</h2>
                      <span className="text-sm text-text-muted">Portfolio stewardship signals</span>
                    </div>
                    {investmentPortfolio.data ? (
                      (() => {
                        const holdings = investmentPortfolio.data.currencies.flatMap(
                          (row) => row.holdings,
                        );
                        const health = {
                          totalHoldings: holdings.length,
                          missingThesis: holdings.reduce(
                            (sum, holding) => sum + holding.missingThesisCount,
                            0,
                          ),
                          reviewDue: holdings.reduce(
                            (sum, holding) => sum + holding.reviewDueCount,
                            0,
                          ),
                          brokenThesis: holdings.reduce(
                            (sum, holding) => sum + holding.brokenThesisCount,
                            0,
                          ),
                          aboveMaximum: holdings.reduce(
                            (sum, holding) => sum + holding.aboveMaximumCount,
                            0,
                          ),
                        };

                        return (
                          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                            <StatCard label="Holdings" value={String(health.totalHoldings)} />
                            <StatCard
                              label="Missing thesis"
                              value={String(health.missingThesis)}
                              tone="bad"
                            />
                            <StatCard
                              label="Review due"
                              value={String(health.reviewDue)}
                              tone="default"
                            />
                            <StatCard
                              label="Broken thesis"
                              value={String(health.brokenThesis)}
                              tone="bad"
                            />
                            <StatCard
                              label="Above max"
                              value={String(health.aboveMaximum)}
                              tone="default"
                            />
                          </div>
                        );
                      })()
                    ) : (
                      <p className="mt-4 text-sm text-text-muted">
                        No portfolio data available yet.
                      </p>
                    )}
                  </section>

                  <section className="rounded border border-border bg-surface p-5">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <h2 className="text-lg font-semibold">Long-term accounts side by side</h2>
                      <span className="text-sm text-text-muted">
                        Every long-term account in one view, plus the combined total
                      </span>
                    </div>
                    <div className="mt-4">
                      {investmentPortfolio.isError ? (
                        <p className="text-sm text-danger">Could not load long-term accounts.</p>
                      ) : investmentPortfolio.data ? (
                        <LongTermAccountsComparison
                          portfolio={investmentPortfolio.data}
                          onSelectAccount={openInvestingAccount}
                        />
                      ) : (
                        <p className="text-sm text-text-muted">Loading long-term accounts...</p>
                      )}
                    </div>
                  </section>

                  <section className="rounded border border-border bg-surface p-5">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <h2 className="text-lg font-semibold">Annual returns</h2>
                      <span className="text-sm text-text-muted">Calendar-year % per account</span>
                    </div>
                    <div className="mt-4">
                      <AccountAnnualReturns
                        accounts={longTermAccounts}
                        longTermReturns={investmentPortfolio.data?.accountReturns}
                        longTermState={longTermState}
                        label="Annual returns for long-term accounts"
                      />
                    </div>
                  </section>

                  <InvestmentPortfolioDashboard onSelectAccount={openInvestingAccount} />
                </div>
              );
            })()
          : null}

        {view === 'all-wealth'
          ? (() => {
              const accountRows = (accounts.data ?? []).map((account, index) => ({
                account,
                summary: accountSummaries[index]?.data ?? null,
              }));
              const currencyTotals = accountRows.reduce<Map<string, number>>((map, row) => {
                const currency = row.account.currency;
                const current = map.get(currency) ?? 0;
                map.set(currency, current + Number(row.summary?.totalMv ?? 0));
                return map;
              }, new Map());
              const totalWealth = accountRows.reduce(
                (sum, row) => sum + Number(row.summary?.totalMv ?? 0),
                0,
              );
              const topAccounts = [...accountRows]
                .sort(
                  (left, right) =>
                    Number(right.summary?.totalMv ?? 0) - Number(left.summary?.totalMv ?? 0),
                )
                .slice(0, 5);
              const themeTotals = (investmentPortfolio.data?.currencies ?? [])
                .flatMap((currency) =>
                  currency.themes.map((theme) => ({
                    currency: currency.currency,
                    theme: theme.theme ?? 'Unclassified',
                    value: Number(theme.value ?? 0),
                    maxValue: Number(currency.accountValue ?? 0),
                  })),
                )
                .sort((left, right) => right.value - left.value)
                .slice(0, 5);
              const mandateMix = [
                {
                  label: 'Swing Trading',
                  value: accountRows.filter((row) => row.account.mandate === 'SWING_TRADING')
                    .length,
                },
                {
                  label: 'Long-Term Investing',
                  value: accountRows.filter((row) => row.account.mandate === 'LONG_TERM_INVESTING')
                    .length,
                },
                {
                  label: 'Unassigned',
                  value: accountRows.filter((row) => !row.account.mandate).length,
                },
              ].filter((row) => row.value > 0);
              const riskAccounts = [...accountRows]
                .filter((row) => row.summary)
                .sort(
                  (left, right) =>
                    Number(right.summary?.totalRiskDollar ?? 0) -
                    Number(left.summary?.totalRiskDollar ?? 0),
                )
                .slice(0, 4);
              const averageHeat =
                accountRows.length === 0
                  ? 0
                  : accountRows.reduce(
                      (sum, row) => sum + Number(row.summary?.portfolioHeatPct ?? 0),
                      0,
                    ) / accountRows.length;

              return (
                <div className="space-y-5">
                  <section className="rounded border border-border bg-surface p-5">
                    <h2 className="text-xl font-semibold">All wealth overview</h2>
                    <p className="mt-1 text-sm text-text-muted">
                      Consolidated exposure by account, mandate and portfolio mix without forcing
                      incompatible cross-currency metrics.
                    </p>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <StatCard label="Accounts" value={String(accounts.data?.length ?? 0)} />
                      <StatCard
                        label="Swing"
                        value={String(
                          (accounts.data ?? []).filter(
                            (account) => account.mandate === 'SWING_TRADING',
                          ).length,
                        )}
                      />
                      <StatCard
                        label="Investing"
                        value={String(
                          (accounts.data ?? []).filter(
                            (account) => account.mandate === 'LONG_TERM_INVESTING',
                          ).length,
                        )}
                      />
                      <StatCard
                        label="Currencies"
                        value={String(currencyTotals.size)}
                        tone="good"
                      />
                    </div>

                    <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
                      <div className="rounded border border-border bg-surface-raised p-4">
                        <h3 className="text-lg font-semibold">Top accounts</h3>
                        <div className="mt-4 space-y-3">
                          {topAccounts.map(({ account, summary }) => {
                            const accountShare =
                              totalWealth > 0
                                ? (Number(summary?.totalMv ?? 0) / totalWealth) * 100
                                : 0;
                            return (
                              <div
                                key={account.id}
                                className="flex items-center justify-between gap-3 rounded border border-border bg-surface px-3 py-2"
                              >
                                <div>
                                  <div className="font-medium">{account.name}</div>
                                  <div className="text-sm text-text-muted">
                                    {account.broker} • {mandateLabel(account.mandate)} •{' '}
                                    {account.currency}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-medium tabular-nums">
                                    {summary
                                      ? formatCurrency(summary.totalMv, account.currency)
                                      : '—'}
                                  </div>
                                  <div className="text-xs uppercase tracking-[0.12em] text-text-muted">
                                    {accountShare.toFixed(1)}% of wealth
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="rounded border border-border bg-surface-raised p-4">
                        <h3 className="text-lg font-semibold">Mandate mix</h3>
                        <div className="mt-4 space-y-3">
                          {mandateMix.length === 0 ? (
                            <p className="text-sm text-text-muted">No mandates assigned yet.</p>
                          ) : (
                            mandateMix.map((item) => {
                              const share =
                                accountRows.length > 0
                                  ? (item.value / accountRows.length) * 100
                                  : 0;
                              return (
                                <div key={item.label} className="space-y-1">
                                  <div className="flex items-center justify-between gap-3 text-sm">
                                    <span className="font-medium">{item.label}</span>
                                    <span className="tabular-nums">{item.value}</span>
                                  </div>
                                  <div className="h-2 overflow-hidden rounded bg-bg-muted">
                                    <div
                                      className="h-full rounded bg-primary"
                                      style={{ width: `${Math.min(100, share)}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
                      <div className="rounded border border-border bg-surface-raised p-4">
                        <h3 className="text-lg font-semibold">Theme mix</h3>
                        <div className="mt-4 space-y-3">
                          {themeTotals.length === 0 ? (
                            <p className="text-sm text-text-muted">
                              No confirmed long-term themes yet. Import HL holdings to begin the
                              portfolio mix.
                            </p>
                          ) : (
                            themeTotals.map(({ currency, theme, value, maxValue }) => (
                              <div key={`${currency}-${theme}`} className="space-y-1">
                                <div className="flex items-center justify-between gap-3 text-sm">
                                  <span className="font-medium">{theme}</span>
                                  <span className="tabular-nums">
                                    {formatCurrency(value, currency)}
                                  </span>
                                </div>
                                <div className="h-2 overflow-hidden rounded bg-bg-muted">
                                  <div
                                    className="h-full rounded bg-primary"
                                    style={{
                                      width: `${Math.min(100, (value / Math.max(maxValue, 1)) * 100)}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      <div className="rounded border border-border bg-surface-raised p-4">
                        <h3 className="text-lg font-semibold">Concentration notes</h3>
                        <ul className="mt-4 space-y-2 text-sm text-text-muted">
                          <li>
                            • Largest account share is measured from confirmed total market value
                            across all selected accounts.
                          </li>
                          <li>
                            • Currency exposures are displayed separately without forcing unverified
                            FX conversion.
                          </li>
                          <li>
                            • Long-term themes are shown only where the portfolio service has a
                            classified primary theme.
                          </li>
                        </ul>
                      </div>
                    </div>

                    <section className="mt-6 rounded border border-border bg-surface-raised p-4">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-lg font-semibold">Risk watchlist</h3>
                        <span className="text-sm text-text-muted">
                          Account-level concentration and heat
                        </span>
                      </div>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <StatCard
                          label="Avg heat"
                          value={`${averageHeat.toFixed(1)}%`}
                          tone={averageHeat > 60 ? 'bad' : 'default'}
                        />
                        <StatCard
                          label="Highest risk"
                          value={
                            riskAccounts[0]?.summary
                              ? formatCurrency(
                                  riskAccounts[0].summary.totalRiskDollar,
                                  riskAccounts[0].account.currency,
                                )
                              : '—'
                          }
                          tone="bad"
                        />
                        <StatCard
                          label="Top account"
                          value={topAccounts[0]?.account.name ?? '—'}
                          tone="good"
                        />
                        <StatCard
                          label="Tracked risk accounts"
                          value={String(riskAccounts.length)}
                        />
                      </div>

                      <div className="mt-4 space-y-3">
                        {riskAccounts.length === 0 ? (
                          <p className="text-sm text-text-muted">
                            No risk data for the selected accounts yet.
                          </p>
                        ) : (
                          riskAccounts.map(({ account, summary }) => (
                            <div
                              key={account.id}
                              className="flex items-center justify-between gap-3 rounded border border-border bg-surface px-3 py-2"
                            >
                              <div>
                                <div className="font-medium">{account.name}</div>
                                <div className="text-sm text-text-muted">
                                  {mandateLabel(account.mandate)} • {account.currency}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-medium tabular-nums">
                                  {summary
                                    ? formatCurrency(summary.totalRiskDollar, account.currency)
                                    : '—'}
                                </div>
                                <div className="text-xs uppercase tracking-[0.12em] text-text-muted">
                                  {summary ? `${summary.portfolioHeatPct}% heat` : 'No heat'}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </section>

                    <div className="mt-6 space-y-3">
                      {[...currencyTotals.entries()].map(([currency, total]) => (
                        <div
                          key={currency}
                          className="flex items-center justify-between rounded border border-border bg-surface-raised px-3 py-2"
                        >
                          <div>
                            <div className="font-medium">{currency}</div>
                            <div className="text-sm text-text-muted">
                              {
                                accountRows.filter((row) => row.account.currency === currency)
                                  .length
                              }{' '}
                              accounts
                            </div>
                          </div>
                          <span className="text-sm tabular-nums text-text-muted">
                            {formatCurrency(total, currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section
                    className="rounded border border-border bg-surface p-5"
                    aria-labelledby="annual-returns-heading"
                  >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <h2 id="annual-returns-heading" className="text-lg font-semibold">
                        Annual returns by account
                      </h2>
                      <span className="text-sm text-text-muted">
                        Calendar-year % for every account, each in its own currency
                      </span>
                    </div>
                    <div className="mt-4">
                      <AccountAnnualReturns
                        accounts={accounts.data ?? []}
                        longTermReturns={investmentPortfolio.data?.accountReturns}
                        longTermState={longTermState}
                        label="Annual returns for all accounts"
                      />
                    </div>
                  </section>
                </div>
              );
            })()
          : null}
      </div>
    </main>
  );

  function toggleSort(nextKey: SymbolSort): void {
    if (sortKey === nextKey) {
      setDescending((value) => !value);
      return;
    }
    setSortKey(nextKey);
    setDescending(true);
  }
}

function SortableHeader({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <TableHeaderCell numeric>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-2 text-text-muted hover:text-text"
        aria-label={`Sort by ${label}`}
      >
        <span>{label}</span>
        <ArrowDownUp className={`h-3.5 w-3.5 ${active ? 'text-text' : ''}`} aria-hidden="true" />
      </button>
    </TableHeaderCell>
  );
}
